import { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useModaleAccessibile } from '../../hooks/useModaleAccessibile';

interface GameOverOverlayProps {
  /** Whether the overlay is shown. */
  open: boolean;
  /** Outcome from the player's point of view, drives the heading colour. */
  result: 'win' | 'loss' | 'draw' | '';
  /** Main heading, e.g. "You won" / "You lost" / "Draw". */
  title: string;
  /** Reason line, e.g. "by resignation" / "opponent left". */
  reason?: string;
  /** Dismiss the overlay to review the final board and reach the rematch actions. */
  onClose: () => void;
  /** Optional extra content rendered above the dismiss button. */
  children?: ReactNode;
}

// Result heading colour by outcome — falls back to the neutral text colour.
const RESULT_COLOR: Record<string, string> = {
  win: 'var(--color-success, #2ecc71)',
  loss: 'var(--color-error, #e74c3c)',
  draw: 'var(--color-text)',
};

/**
 * Game-over overlay sized like the "Resign?" confirmation dialog: a centred
 * `.auth-modal` card over a dimmed backdrop. It makes the end of the game
 * unmistakable — previously the only cue was a small turn-text line, so when an
 * opponent left the player could be left staring at a dead, still-rendered board.
 * Dismissable (backdrop / close) to inspect the final position.
 */
function GameOverOverlay({ open, result, title, reason, onClose, children }: GameOverOverlayProps) {
  const { t } = useTranslation();

  /*
   * Dialogo dichiarato e fuoco governato. Chiamato prima del `return null` qui
   * sotto perché un hook non può stare dietro a una condizione: `open` è il suo
   * interruttore.
   */
  const riferimentoDialogo = useModaleAccessibile<HTMLDivElement>(open, onClose);

  if (!open) return null;

  return createPortal(
    <div
      className="auth-modal-overlay auth-modal-overlay--conferma"
      onClick={onClose}
      data-testid="game-over-overlay"
    >
      <div
        ref={riferimentoDialogo}
        className="auth-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="game-over-overlay-titolo"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 360, textAlign: 'center' }}
      >
        <button
          type="button"
          className="auth-modal-close"
          onClick={onClose}
          aria-label={t('common.close', 'Close')}
          style={{ minWidth: 44, minHeight: 44 }}
        >
          <X size={20} aria-hidden="true" />
        </button>

        <h2
          id="game-over-overlay-titolo"
          style={{
            marginBottom: reason ? 'var(--spacing-xs)' : 'var(--spacing-lg)',
            fontSize: '1.5rem',
            color: RESULT_COLOR[result] ?? 'var(--color-text)',
          }}
        >
          {title}
        </h2>

        {reason && (
          <p
            style={{
              color: 'var(--color-text-secondary)',
              fontSize: '0.95rem',
              marginBottom: 'var(--spacing-lg)',
            }}
          >
            {reason}
          </p>
        )}

        {children}

        <button
          type="button"
          data-testid="game-over-continue"
          onClick={onClose}
          style={{
            width: '100%',
            padding: 'var(--spacing-md)',
            borderRadius: 8,
            border: 'none',
            background: 'var(--color-primary, #4a7c59)',
            color: 'white',
            fontSize: '1rem',
            fontWeight: 600,
            cursor: 'pointer',
            minHeight: 44,
          }}
        >
          {t('common.continue', 'Continue')}
        </button>
      </div>
    </div>,
    document.body,
  );
}

export default GameOverOverlay;
